import React, { useEffect, useMemo, useState } from "react";
import permissionsIcon from "../../../assets/permissionsVector.png";
import { Search, CheckCircle2 } from "lucide-react";
import { rbacService } from "../../../services/rbac.service";

const PINK = "#d86a78";
const BORDER = "#f2c6cc";

const MODULE_LABELS = {
  admin: "Admin Panel",
  dashboard: "Dashboard",
  employees: "Employees",
  rbac: "Roles & Permissions",
  warehouses: "Warehouse",
  products: "Products",
  inventory: "Inventory",
  reports: "Reports",
  settings: "Settings",
  posters: "Posters",
  short_videos: "Short Videos",
  broadcasts: "Broadcast Messages",
  driver_wallet: "Driver Wallet",
  app_layout: "App Layout",
  warehouse: "Warehouse Panel",
  wh: "Warehouse Panel",
  delivery: "Delivery Partners",
  users: "Users",
};

const ACTION_LABELS = {
  read: "View",
  view: "View",
  create: "Create",
  update: "Edit",
  manage: "Manage",
  delete: "Delete",
  approve: "Approve",
  export: "Export",
  access: "Access",
};

function humanize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function permissionModule(key = "") {
  const parts = String(key || "").split(".").filter(Boolean);
  if (parts[0] === "admin" && parts[1]) return parts[1];
  if (parts[0] === "warehouse" && parts[1] === "panel") return "warehouse";
  return parts[0] || "other";
}

function permissionLabel(key = "") {
  const parts = String(key || "").split(".").filter(Boolean);
  const moduleKey = permissionModule(key);
  const action = parts[parts.length - 1] || "";
  const moduleName = MODULE_LABELS[moduleKey] || humanize(moduleKey);
  const actionName = ACTION_LABELS[action] || humanize(action);

  if (action === "access") return `${moduleName} Access`;
  if (moduleKey === "rbac" && parts.includes("roles")) return `${actionName} Roles`;
  if (moduleKey === "rbac" && parts.includes("permissions")) return `${actionName} Permissions`;
  if (moduleKey === "dashboard") return "View Dashboard";
  if (moduleKey === "app_layout") return `${actionName} App Layout`;
  if (moduleKey === "driver_wallet") return `${actionName} Driver Wallet`;
  return `${actionName} ${moduleName}`;
}

function Switch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative h-[20px] w-[46px] shrink-0 rounded-full transition focus:outline-none"
      style={{ background: checked ? PINK : "#E6E6E6" }}
    >
      <span
        className="absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white shadow transition"
        style={{ left: checked ? "28px" : "2px" }}
      />
    </button>
  );
}

function PermissionRow({ permission, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-[12px] border border-slate-100 bg-white px-4 py-3">
      <div>
        <div className="text-[13px] font-semibold text-slate-700">{permissionLabel(permission.key)}</div>
        {permission.description ? (
          <div className="mt-1 text-[11px] leading-4 text-slate-400">{permission.description}</div>
        ) : null}
      </div>
      <Switch checked={checked} onChange={onChange} />
    </div>
  );
}

function targetTitle(target) {
  if (target === "admin_employee") return "Admin Panel Permissions";
  if (target === "warehouse_employee") return "Warehouse Panel Permissions";
  return "Shared Permissions";
}

function permissionKeyOf(permission = {}) {
  return String(permission.key || permission.permKey || permission.perm_key || "")
    .trim()
    .toLowerCase();
}

function appliesToOf(permission = {}) {
  return String(permission.appliesTo || permission.applies_to || "")
    .trim()
    .toLowerCase();
}

function isWarehousePermission(permission = {}) {
  const key = permissionKeyOf(permission);
  const appliesTo = appliesToOf(permission);

  return (
    appliesTo === "warehouse_employee" ||
    key.startsWith("wh.") ||
    key.startsWith("warehouse.")
  );
}

function isAdminPermission(permission = {}) {
  const key = permissionKeyOf(permission);
  const appliesTo = appliesToOf(permission);

  return (
    (
      appliesTo === "admin_employee" ||
      appliesTo === "both" ||
      key.startsWith("admin.") ||
      key.startsWith("rbac.") ||
      key === "dashboard.view" ||
      key.startsWith("employees.") ||
      key.startsWith("products.") ||
      key.startsWith("warehouses.") ||
      key.startsWith("users.") ||
      key.startsWith("delivery.")
    ) && !isWarehousePermission(permission)
  );
}

function filterPermissionsByTarget(permissions = [], target = "both") {
  if (target === "warehouse_employee") return permissions.filter(isWarehousePermission);
  if (target === "admin_employee") return permissions.filter(isAdminPermission);
  return permissions;
}

export default function PermissionsDrawer({ open, onClose, value, onSave, target = "both" }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        // Load the full catalog, then filter in the same way as employee create/edit.
        // This avoids hiding old permissions whose DB applies_to value was seeded incorrectly.
        const data = await rbacService.listPermissions({});
        if (!mounted) return;

        setItems(filterPermissionsByTarget(Array.isArray(data) ? data : [], target));
        const incoming = Array.isArray(value)
          ? value
          : Array.isArray(value?.permissionIds)
          ? value.permissionIds
          : [];
        setSelectedIds(incoming);
      } catch (e) {
        if (!mounted) return;
        setItems([]);
        setError(e?.message || "Failed to load permissions");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, target, value]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    if (!open) return;

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollBarWidth}px`;

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const filteredItems = useMemo(() => {
    const qq = search.trim().toLowerCase();
    if (!qq) return items;
    return items.filter((item) => {
      const label = permissionLabel(item.key);
      const moduleName = MODULE_LABELS[permissionModule(item.key)] || "";
      const hay = `${item.key || ""} ${item.description || ""} ${label} ${moduleName}`.toLowerCase();
      return hay.includes(qq);
    });
  }, [items, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    filteredItems.forEach((item) => {
      const moduleKey = permissionModule(item.key);
      if (!map.has(moduleKey)) map.set(moduleKey, []);
      map.get(moduleKey).push(item);
    });
    return Array.from(map.entries()).sort(([a], [b]) => {
      const aa = MODULE_LABELS[a] || humanize(a);
      const bb = MODULE_LABELS[b] || humanize(b);
      return aa.localeCompare(bb);
    });
  }, [filteredItems]);

  const toggle = (id) => (nextValue) => {
    setSelectedIds((prev) => {
      const set = new Set(prev || []);
      if (nextValue) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const setGroup = (permissions, checked) => {
    const ids = permissions.map((p) => p.id).filter(Boolean);
    setSelectedIds((prev) => {
      const set = new Set(prev || []);
      ids.forEach((id) => {
        if (checked) set.add(id);
        else set.delete(id);
      });
      return Array.from(set);
    });
  };

  return (
    <>
      <div
        className={
          "fixed inset-0 z-[60] transition " +
          (open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")
        }
        onClick={onClose}
        style={{
          background: "rgba(255,255,255,0.28)",
          backdropFilter: "blur(3px)",
        }}
      />

      <div
        className={
          "fixed right-0 top-0 z-[70] h-full w-[560px] max-w-[96vw] bg-white shadow-2xl transition-transform duration-300 " +
          (open ? "translate-x-0" : "translate-x-full")
        }
        style={{ borderLeft: `2px solid ${BORDER}` }}
      >
        <div className="flex h-full flex-col px-6 pt-8">
          <div className="flex items-center justify-center gap-2.5">
            <img src={permissionsIcon} alt="permissions" className="h-[18px] w-[18px] select-none" draggable={false} />
            <div className="text-[16px] font-semibold tracking-wide" style={{ color: PINK }}>
              Select Permissions
            </div>
          </div>

          <div className="mt-4 h-px w-full" style={{ background: BORDER }} />

          <div className="mt-5 rounded-[12px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3 text-[12px] text-slate-500">
            {target === "admin_employee"
              ? "Only admin panel permissions are shown for this role."
              : target === "warehouse_employee"
              ? "Only warehouse panel permissions are shown for this role."
              : "Shared permissions are shown for this role."}
          </div>

          <div className="relative mt-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by module or permission name"
              className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] bg-white pl-10 pr-4 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          </div>

          <div className="mt-5 flex-1 overflow-y-auto pb-4 pr-1">
            {loading ? (
              <div className="py-6 text-center text-[13px] text-slate-400">Loading permissions...</div>
            ) : error ? (
              <div className="py-6 text-center text-[13px] text-red-500">{error}</div>
            ) : grouped.length === 0 ? (
              <div className="py-6 text-center text-[13px] text-slate-400">No permissions found.</div>
            ) : (
              <div className="space-y-4">
                {grouped.map(([moduleKey, permissions]) => {
                  const selectedCount = permissions.filter((p) => selectedIds.includes(p.id)).length;
                  const allSelected = permissions.length > 0 && selectedCount === permissions.length;

                  return (
                    <div key={moduleKey} className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[13px] font-bold text-[#d86a78]">
                            {MODULE_LABELS[moduleKey] || humanize(moduleKey)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                            <CheckCircle2 size={13} /> {selectedCount}/{permissions.length} selected
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setGroup(permissions, !allSelected)}
                          className="rounded-full border border-[#f2c6cc] bg-white px-3 py-1 text-[11px] font-semibold text-[#d86a78]"
                        >
                          {allSelected ? "Remove All" : "Select All"}
                        </button>
                      </div>

                      <div className="space-y-2">
                        {permissions.map((permission) => (
                          <PermissionRow
                            key={permission.id}
                            permission={permission}
                            checked={selectedIds.includes(permission.id)}
                            onChange={toggle(permission.id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 py-5">
            <div className="mb-3 text-center text-[12px] text-slate-500">
              {selectedIds.length} permission{selectedIds.length === 1 ? "" : "s"} selected
            </div>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="h-[36px] w-[130px] rounded-[7px] border border-[#f2c6cc] bg-white text-[13px] font-semibold text-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onSave?.(selectedIds || []);
                  onClose?.();
                }}
                className="h-[36px] w-[130px] rounded-[7px] text-[13px] font-semibold text-white"
                style={{ background: PINK }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { permissionLabel };
